import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap, toArray } from 'rxjs/operators';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
	Address,
	ConsumptionInfoReceiverService,
	OwnerService,
	Person,
	PersonService,
	Resident,
	ResidentService,
	TenantService,
	Vacancy,
	VacancyService,
} from '@eadsysteme/ewsrealestateclient-v3';
import { EnergyEfficiencySettingService, RoleSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { RoleType, UiRoleType } from '../../../../services/RoleModel';
import { Permission } from '../../../../services/data/permission';
import { PermissionsService } from '../../../../services/permissions.service';
import { DateUtil } from '../../../../util/DateUtil';
import { PersonDisplayUtil } from '../../../../util/PersonDisplayUtil';
import { RolesDisplayService } from '../roles-display.service';
import { isSyntheticPersonId } from '../roles-view-model';
import { PersistedRole, PersonEditorRoleValue, PortalAccessAction, StrandContext, StrandNeighbour } from './person-editor-dialog.types';
import { canHavePortalAccess, permissionTypeForRole } from '../../../../util/role-permission-type';
import { StrandContextFactory } from './strand-context.factory';
import CountryEnum = Address.CountryEnum;
import RoleTypeEnum = RoleSetting.RoleTypeEnum;
import ReceptionTypeEnum = RoleSetting.ReceptionTypeEnum;

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export interface TranslatedCountry {
	value: CountryEnum;
	translation: string;
}

/** Schreibreihenfolge der Stränge; Leerstand läuft unter Bewohner. */
const STRAND_ORDER: RoleType[] = [UiRoleType.OWNER, UiRoleType.RESIDENT, UiRoleType.TENANT, UiRoleType.CONSUMPTION_INFO_RECEIVER];

export interface PersonEditorValidation {
	/** Harte Verstöße — blockieren das Speichern. */
	hard: string[];
}

export interface PersistRolesRequest {
	area: number;
	realEstateId: string;
	tenantUnitId: string;
	person: Person;
	isEdit: boolean;
	skipPersonPersist: boolean;
	roles: PersonEditorRoleValue[];
	strands: StrandContext[];
	/** Abweichende Bis-Daten der Nachbarrollen (Schlüssel = deren `roleId`). */
	boundaryOverrides: Map<string, Dayjs>;
	/**
	 * Vorhandener Zugangscode je Rollen-Id. Ist einer dabei, verschickt der Auth-Service genau DIESEN
	 * Code erneut; fehlt er, entsteht ein neuer. Ohne diese Unterscheidung würde „erneut versenden"
	 * bereits ausgedruckte Codes entwerten.
	 */
	portalAccessCodeByRoleId?: Record<string, string>;
}

/** Eingabe der Folgen-Vorschau — alles, was das Speichern über die reinen Rollenzeilen hinaus auslöst. */
export interface ConsequenceInput {
	roles: PersonEditorRoleValue[];
	strands: StrandContext[];
	boundaryOverrides: Map<string, Dayjs>;
	/** Liegt eine E-Mail-Adresse vor? Nur dann geht zum Zugangscode auch eine Mail raus. */
	hasMail: boolean;
	/**
	 * Was je Rolle mit dem Zugangscode passiert. Fehlt eine Rolle darin (weil sie noch keine Id hat),
	 * entsteht ein neuer Code. Der Folgen-Text muss „neu erstellt", „vorhandener Code" und „erneut
	 * verschickt" auseinanderhalten — ein vorhandener, nie verschickter Code ist eben kein neuer.
	 */
	portalAccessActionByRoleId: ReadonlyMap<string, PortalAccessAction>;
	isEdit: boolean;
}

export interface PersistRolesResult {
	person: Person;
	roles: PersistedRole[];
}

/** Eine Formular-Rolle samt ihrem Persistenz-Ergebnis. */
export interface SavedRole {
	role: PersonEditorRoleValue;
	saved?: PersistedRole;
}

/** Zeitraum einer Formularzeile; `end === null` heißt offenes Ende. */
interface StrandInterval {
	start: Dayjs;
	end: Dayjs | null;
	isNew: boolean;
}

/** Fachlogik des Personen-/Rollen-Editors: DTO-Mapping, sequenzielle Persistenz, Strang-Validierung, Folgen, Länderliste. */
@Injectable({ providedIn: 'root' })
export class PersonRolesEditorService {
	private readonly personService = inject(PersonService);
	private readonly residentService = inject(ResidentService);
	private readonly tenantService = inject(TenantService);
	private readonly ownerService = inject(OwnerService);
	private readonly consumptionInfoReceiverService = inject(ConsumptionInfoReceiverService);
	private readonly vacancyService = inject(VacancyService);
	private readonly energyEfficiencySettingService = inject(EnergyEfficiencySettingService);
	private readonly permissionsService = inject(PermissionsService);
	private readonly dateUtil = inject(DateUtil);
	private readonly transloco = inject(TranslocoService);
	private readonly personDisplay = inject(PersonDisplayUtil);
	private readonly rolesDisplay = inject(RolesDisplayService);

	// ---------------------------------------------------------------------------------------------
	// Person-Aufbereitung
	// ---------------------------------------------------------------------------------------------

	getEmptyAddress(): Address {
		return { streets: [{ streetName: '', houseNumber: '' }], zipCode: '', city: '' };
	}

	splitFullName(person: Person): { givenName: string; familyName: string } {
		if (person.givenName || person.familyName) {
			return { givenName: person.givenName ?? '', familyName: person.familyName ?? '' };
		}
		return this.personDisplay.splitFullName(person.fullName);
	}

	isFullNameOnlySalutation(salutation: string | null | undefined): boolean {
		return salutation === Person.SalutationEnum.DIV || salutation === Person.SalutationEnum.COMPANY || salutation === Person.SalutationEnum.FAMILY;
	}

	/** Baut das zu persistierende Person-Objekt aus den Formularwerten. */
	buildPerson(
		base: Person,
		values: {
			salutation: Person.SalutationEnum | null;
			title: string | null;
			givenName: string | null;
			familyName: string | null;
			fullName: string | null;
			email: string | null;
			showAddress: boolean;
			street: string | null;
			houseNumber: string | null;
			zipCode: string | null;
			city: string | null;
			country: CountryEnum | null;
		}
	): Person {
		const person: Person = base ? structuredClone(base) : {};
		person.salutation = values.salutation ?? undefined;
		person.title = values.title ?? undefined;

		if (this.isFullNameOnlySalutation(values.salutation)) {
			person.fullName = values.fullName ?? undefined;
			person.givenName = undefined;
			person.familyName = undefined;
		} else {
			person.givenName = values.givenName ?? undefined;
			person.familyName = values.familyName ?? undefined;
			person.fullName = [values.givenName, values.familyName].filter((v) => v?.trim()).join(' ') || (values.fullName ?? undefined);
		}

		// Das E-Mail-Feld ist im Editor immer sichtbar und mit dem Bestandswert vorbelegt — der
		// Formularwert ist damit maßgeblich (auch das bewusste Leeren).
		person.email = values.email ?? undefined;

		if (values.showAddress) {
			const address = person.address ?? this.getEmptyAddress();
			address.streets = address.streets?.length ? address.streets : [{ streetName: '', houseNumber: '' }];
			address.streets[0].streetName = values.street ?? '';
			address.streets[0].houseNumber = values.houseNumber ?? '';
			address.city = values.city ?? '';
			address.zipCode = values.zipCode ?? '';
			address.country = values.country ?? undefined;
			person.address = address;
		} else if (this.hasAddressContent(person.address)) {
			person.address = { ...person.address, streets: [], zipCode: '', city: '' };
		}

		this.trimPerson(person);
		return person;
	}

	private hasAddressContent(address: Address | undefined): boolean {
		const street = address?.streets?.[0];
		return !!(street?.streetName?.trim() || street?.houseNumber?.trim() || address?.zipCode?.trim() || address?.city?.trim());
	}

	private trimPerson(person: Person): void {
		person.title = person.title?.trim();
		person.givenName = person.givenName?.trim();
		person.familyName = person.familyName?.trim();
		person.fullName = person.fullName?.trim();
		person.email = person.email?.trim();
		const street = person.address?.streets?.[0];
		if (street) {
			street.streetName = street.streetName?.trim();
			street.houseNumber = street.houseNumber?.trim();
		}
		if (person.address) {
			person.address.city = person.address.city?.trim();
			person.address.zipCode = person.address.zipCode?.trim();
		}
	}

	// ---------------------------------------------------------------------------------------------
	// Folgen
	// ---------------------------------------------------------------------------------------------

	/** Klartext-Vorschau aller Auswirkungen. */
	buildConsequences(input: ConsequenceInput): string[] {
		const lines: string[] = [];
		const t = (key: string, params?: Record<string, unknown>): string => this.transloco.translate(key, params);

		lines.push(...this.endedPredecessorLines(input));

		const portalAccessRoles = input.roles.filter((role) => role.portalAccess && canHavePortalAccess(role.roleType));
		const actionOf = (role: PersonEditorRoleValue): PortalAccessAction => (role.id && input.portalAccessActionByRoleId.get(role.id)) || 'new';
		const newCodeRoles = portalAccessRoles.filter((role) => actionOf(role) === 'new');
		// Nur ein neuer Code heisst auch neue Berechtigung — bei vorhandenem Code entsteht nichts.
		if (newCodeRoles.length > 0) {
			lines.push(t('tenantunitDetails.editor.consequence.permissions', { count: newCodeRoles.length }));
			if (input.hasMail) {
				lines.push(t('tenantunitDetails.editor.consequence.accessCodeNew'));
			}
		}
		if (portalAccessRoles.some((role) => actionOf(role) === 'existing') && input.hasMail) {
			lines.push(t('tenantunitDetails.editor.consequence.accessCodeExisting'));
		}
		// Ohne Adresse kann kein Code raus — sonst bliebe die Vorschau zum gesetzten Häkchen stumm.
		if (portalAccessRoles.length > 0 && !input.hasMail) {
			lines.push(t('tenantunitDetails.editor.consequence.accessCodeNoMail'));
		}
		// `resend` entsteht nur bei passender Adresse — ein hasMail-Test wäre hier immer wahr.
		if (portalAccessRoles.some((role) => actionOf(role) === 'resend')) {
			lines.push(t('tenantunitDetails.editor.consequence.accessCodeResent'));
		}

		if (input.isEdit) {
			lines.push(t('tenantunitDetails.editor.consequence.personDataScope'));
		}

		return [...new Set(lines)];
	}

	/** `adjustMoveOutDate` setzt das Ende des Vorgängers bedingungslos — das beendet, verkürzt oder verlängert ihn. */
	private endedPredecessorLines(input: ConsequenceInput): string[] {
		const lines: string[] = [];
		for (const strand of input.strands) {
			const previous = strand.previous;
			const earliestNew = input.roles
				.filter((role) => !role.id && !!role.roleType && StrandContextFactory.strandTypeOf(role.roleType) === strand.roleType)
				.map((role) => role.validFrom)
				.filter((validFrom): validFrom is Dayjs => !!validFrom)
				.sort((a, b) => a.valueOf() - b.valueOf())[0];
			if (!previous || !earliestNew) {
				continue;
			}
			const line = this.predecessorLine(previous, input.boundaryOverrides.get(previous.roleId) ?? earliestNew.subtract(1, 'day'));
			if (line) {
				lines.push(line);
			}
		}
		return lines;
	}

	private predecessorLine(previous: StrandNeighbour, newEnd: Dayjs): string | null {
		const params = {
			name: previous.name.trim(),
			role: this.getRoleTypeLabel(previous.roleType),
			date: this.dateUtil.checkDateValid(newEnd),
			previousDate: previous.validTo ? this.dateUtil.checkDateValid(previous.validTo) : '',
		};

		if (!previous.validTo) {
			return this.transloco.translate('tenantunitDetails.editor.consequence.predecessorEnded', params);
		}
		if (previous.validTo.isSame(newEnd, 'day')) {
			return null;
		}
		return this.transloco.translate(
			newEnd.isBefore(previous.validTo, 'day')
				? 'tenantunitDetails.editor.consequence.predecessorShortened'
				: 'tenantunitDetails.editor.consequence.predecessorExtended',
			params
		);
	}

	// ---------------------------------------------------------------------------------------------
	// Validierung
	// ---------------------------------------------------------------------------------------------

	/** Prüft je Strang intern und gegen die Nachbarn; Lücken sind nur im Belegungsstrang verboten. */
	validate(roles: PersonEditorRoleValue[], strands: StrandContext[], boundaryOverrides: Map<string, Dayjs> = new Map()): PersonEditorValidation {
		const hard: string[] = [];

		for (const role of roles) {
			if (role.validFrom && role.validTo && !dayjs(role.validTo).isAfter(dayjs(role.validFrom), 'day')) {
				hard.push(this.transloco.translate('tenantunitDetails.editor.validation.endAfterStart', { role: this.getRoleTypeLabel(role.roleType) }));
			}
		}

		for (const strandType of this.strandTypesOf(roles)) {
			const own = this.ownIntervals(roles, strandType);
			this.checkInternalOverlap(own, strandType, hard);
			const strand = strands.find((entry) => entry.roleType === strandType);
			if (strand) {
				this.checkNeighbours(own, strand, hard, boundaryOverrides);
			}
		}

		return { hard: [...new Set(hard)] };
	}

	private strandTypesOf(roles: PersonEditorRoleValue[]): RoleType[] {
		const types = roles
			.map((role) => role.roleType)
			.filter((roleType): roleType is RoleType => !!roleType)
			.map((roleType) => StrandContextFactory.strandTypeOf(roleType));
		return [...new Set(types)];
	}

	/** Ein leeres Von-Datum heißt „seit immer" und wird als minDate geprüft; leeres Bis bleibt offen. */
	private ownIntervals(roles: PersonEditorRoleValue[], strandType: RoleType): StrandInterval[] {
		return roles
			.filter((role) => !!role.roleType && StrandContextFactory.strandTypeOf(role.roleType) === strandType)
			.map((role) => ({
				start: role.validFrom ? dayjs(role.validFrom) : DateUtil.minDate,
				end: role.validTo ? dayjs(role.validTo) : null,
				isNew: !role.id,
			}))
			.sort((a, b) => a.start.valueOf() - b.start.valueOf());
	}

	private checkInternalOverlap(own: StrandInterval[], strandType: RoleType, hard: string[]): void {
		const role = this.getRoleTypeLabel(strandType);
		if (own.filter((interval) => interval.end === null).length > 1) {
			hard.push(this.transloco.translate('tenantunitDetails.editor.validation.multipleOpen', { role }));
		}
		for (let i = 1; i < own.length; i += 1) {
			const previousEnd = own[i - 1].end ?? DateUtil.maxDate;
			if (own[i].start.isSameOrBefore(previousEnd, 'day')) {
				hard.push(this.transloco.translate('tenantunitDetails.editor.validation.sameTypeOverlap', { role }));
			}
		}
	}

	/** Ein offenes Ende des Vorgängers ist der Normalfall vor dem Speichern — das Backend beendet ihn beim Anlegen nahtlos. */
	private checkNeighbours(own: StrandInterval[], strand: StrandContext, hard: string[], boundaryOverrides: Map<string, Dayjs>): void {
		if (own.length === 0) {
			return;
		}
		const first = own[0];
		const last = own.at(-1) ?? first;
		const role = this.getRoleTypeLabel(strand.roleType);

		if (strand.previous) {
			this.checkPredecessor(first, strand.previous, role, this.effectiveEnd(strand.previous, boundaryOverrides), hard);
		}
		if (strand.next) {
			this.checkSuccessor(own, last, strand.next, role, hard);
		}
		if (StrandContextFactory.isOccupancyStrand(strand.roleType)) {
			this.checkOccupancyGaps(first, last, strand, this.effectiveEnd(strand.previous, boundaryOverrides), hard);
		}
	}

	/** Das in der Kontextzeile gesetzte Ende gilt, sonst das heute hinterlegte. */
	private effectiveEnd(neighbour: StrandNeighbour | undefined, boundaryOverrides: Map<string, Dayjs>): Dayjs | null {
		if (!neighbour) {
			return null;
		}
		return boundaryOverrides.get(neighbour.roleId) ?? neighbour.validTo;
	}

	/** Eine NEUE Zeile darf im Zeitraum des Vorgängers beginnen — `adjustMoveOutDate` verkürzt ihn dabei. */
	private checkPredecessor(first: StrandInterval, previous: StrandNeighbour, role: string, previousEnd: Dayjs | null, hard: string[]): void {
		const params = { role, name: previous.name, date: this.dateUtil.checkDateValid(previous.validFrom) };

		if (!first.start.isSame(DateUtil.minDate) && !first.start.isAfter(previous.validFrom, 'day')) {
			hard.push(
				this.transloco.translate(
					first.isNew ? 'tenantunitDetails.editor.validation.newRoleBeforeSuccessor' : 'tenantunitDetails.editor.validation.startBeforePredecessor',
					params
				)
			);
			return;
		}
		if (!first.isNew && previousEnd && !first.start.isAfter(previousEnd, 'day')) {
			hard.push(
				this.transloco.translate('tenantunitDetails.editor.validation.overlapsPredecessor', {
					role,
					name: previous.name,
					date: this.dateUtil.checkDateValid(previousEnd),
				})
			);
		}
	}

	private checkOccupancyGaps(first: StrandInterval, last: StrandInterval, strand: StrandContext, previousEnd: Dayjs | null, hard: string[]): void {
		if (previousEnd && first.start.isAfter(previousEnd.add(1, 'day'), 'day')) {
			hard.push(
				this.transloco.translate('tenantunitDetails.editor.validation.occupancyGap', {
					from: this.dateUtil.checkDateValid(previousEnd.add(1, 'day')),
					to: this.dateUtil.checkDateValid(first.start.subtract(1, 'day')),
				})
			);
		}
		// Die Überschneidung mit dem Nachfolger meldet bereits checkSuccessor.
		if (!strand.next || !last.end?.isBefore(strand.next.validFrom, 'day')) {
			return;
		}
		if (!strand.next.validFrom.isSame(last.end.add(1, 'day'), 'day')) {
			hard.push(
				this.transloco.translate('tenantunitDetails.editor.validation.occupancyGap', {
					from: this.dateUtil.checkDateValid(last.end.add(1, 'day')),
					to: this.dateUtil.checkDateValid(strand.next.validFrom.subtract(1, 'day')),
				})
			);
		}
	}

	/** `create<Rolle>` hängt immer an die zuletzt beginnende Rolle an — vor einem Nachfolger geht nichts. */
	private checkSuccessor(own: StrandInterval[], last: StrandInterval, next: StrandNeighbour, role: string, hard: string[]): void {
		const params = { role, name: next.name, date: this.dateUtil.checkDateValid(next.validFrom) };

		if (own.some((interval) => interval.isNew)) {
			hard.push(this.transloco.translate('tenantunitDetails.editor.validation.newRoleBeforeSuccessor', params));
		}
		if (!last.start.isBefore(next.validFrom, 'day')) {
			hard.push(this.transloco.translate('tenantunitDetails.editor.validation.startAfterSuccessor', params));
			return;
		}
		if (!last.end?.isBefore(next.validFrom, 'day')) {
			hard.push(this.transloco.translate('tenantunitDetails.editor.validation.overlapsSuccessor', params));
		}
	}

	// ---------------------------------------------------------------------------------------------
	// Persistenz
	// ---------------------------------------------------------------------------------------------

	/** Person, dann Rollen, dann uVI-Zustellart und Auto-Permissions. */
	persist(request: PersistRolesRequest): Observable<PersistRolesResult> {
		const person$: Observable<Person> = request.skipPersonPersist ? of(request.person) : this.createOrUpdatePerson(request);

		return person$.pipe(switchMap((person) => this.persistRoles({ ...request, person })));
	}

	private createOrUpdatePerson(request: PersistRolesRequest): Observable<Person> {
		const { area, realEstateId, tenantUnitId, person } = request;
		return request.isEdit && person.id
			? this.personService.updatePerson(area, realEstateId, tenantUnitId, person.id, person)
			: this.personService.createPerson(area, realEstateId, tenantUnitId, person);
	}

	/** Schreibt streng nacheinander und bricht beim ersten Fehler ab. */
	private persistRoles(request: PersistRolesRequest): Observable<PersistRolesResult> {
		const { person } = request;
		const ordered = [...request.roles].sort((a, b) => this.strandRank(a.roleType) - this.strandRank(b.roleType));
		if (ordered.length === 0 && request.boundaryOverrides.size === 0) {
			return of({ person, roles: [] });
		}

		const saved = new Map<PersonEditorRoleValue, PersistedRole>();

		return this.applyBoundaryOverrides(request, 'shrinking').pipe(
			concatMap(() =>
				from(ordered).pipe(
					concatMap((role) => this.createOrUpdateRole(role, request, person).pipe(tap((result) => saved.set(role, result)))),
					toArray()
				)
			),
			concatMap(() => this.applyBoundaryOverrides(request, 'growing')),
			concatMap(() =>
				this.persistResidentRoleSettings(
					ordered.map((role) => ({ role, saved: saved.get(role) })),
					request.area
				)
			),
			concatMap(() => this.createPermissionsForSelectedRoles(request, saved)),
			map(() => ({ person, roles: request.roles.map((role) => saved.get(role)).filter((entry): entry is PersistedRole => !!entry) }))
		);
	}

	/** Eigentümer → Bewohner/Leerstand → Mieter → uVI-Empfänger. */
	private strandRank(roleType: RoleType | null): number {
		if (!roleType) {
			return STRAND_ORDER.length;
		}
		const index = STRAND_ORDER.indexOf(StrandContextFactory.strandTypeOf(roleType));
		return index === -1 ? STRAND_ORDER.length : index;
	}

	/** Verkürzen vor dem Anlegen, Verlängern danach — sonst überschneidet es sich zwischen zwei Calls. */
	private applyBoundaryOverrides(request: PersistRolesRequest, phase: 'shrinking' | 'growing'): Observable<unknown> {
		const neighbours = this.neighboursById(request.strands);
		const overrides: { neighbour: StrandNeighbour; validTo: Dayjs }[] = [];

		for (const [roleId, validTo] of request.boundaryOverrides) {
			const neighbour = neighbours.get(roleId);
			if (!neighbour) {
				continue;
			}
			const shrinks = !!neighbour.validTo && validTo.isBefore(neighbour.validTo, 'day');
			if (shrinks === (phase === 'shrinking')) {
				overrides.push({ neighbour, validTo });
			}
		}

		if (overrides.length === 0) {
			return of(null);
		}

		return from(overrides).pipe(
			concatMap(({ neighbour, validTo }) => this.updateNeighbourEnd(neighbour, validTo, request)),
			toArray()
		);
	}

	private neighboursById(strands: StrandContext[]): Map<string, StrandNeighbour> {
		const entries = strands
			.flatMap((strand) => [strand.previous, strand.next])
			.filter((neighbour): neighbour is StrandNeighbour => !!neighbour)
			.map((neighbour) => [neighbour.roleId, neighbour] as const);
		return new Map(entries);
	}

	// Die Person MUSS mit: der Backend-Mapper liest ungeprueft roleDto.getPerson().getId().
	private updateNeighbourEnd(neighbour: StrandNeighbour, validTo: Dayjs, request: PersistRolesRequest): Observable<PersistedRole> {
		const { area, realEstateId, tenantUnitId } = request;
		const body: Resident = {
			tenantUnitId,
			person: neighbour.person,
			address: neighbour.person?.address,
			validFrom: this.dateUtil.getDateWithoutTimezone(neighbour.validFrom).toISOString(),
			validTo: this.dateUtil.getDateWithoutTimezone(validTo).toISOString(),
		};
		switch (neighbour.roleType) {
			case UiRoleType.RESIDENT:
				return this.residentService.updateResident(area, realEstateId, tenantUnitId, neighbour.roleId, body);
			case UiRoleType.TENANT:
				return this.tenantService.updateTenant(area, realEstateId, tenantUnitId, neighbour.roleId, body);
			case UiRoleType.OWNER:
				return this.ownerService.updateOwner(area, realEstateId, tenantUnitId, neighbour.roleId, body);
			case UiRoleType.CONSUMPTION_INFO_RECEIVER:
				return this.consumptionInfoReceiverService.updateConsumptionInfoReceiver(area, realEstateId, tenantUnitId, neighbour.roleId, body);
			case UiRoleType.VACANCY:
				return this.vacancyService.updateVacancy(area, realEstateId, tenantUnitId, neighbour.roleId, body);
			default:
				return of({} as PersistedRole);
		}
	}

	private createOrUpdateRole(role: PersonEditorRoleValue, request: PersistRolesRequest, person: Person): Observable<PersistedRole> {
		const { area, realEstateId, tenantUnitId } = request;
		const body = this.buildRoleDto(role, person, tenantUnitId);
		switch (role.roleType) {
			case UiRoleType.RESIDENT:
				return role.id
					? this.residentService.updateResident(area, realEstateId, tenantUnitId, role.id, body)
					: this.residentService.createResident(area, realEstateId, tenantUnitId, body);
			case UiRoleType.TENANT:
				return role.id
					? this.tenantService.updateTenant(area, realEstateId, tenantUnitId, role.id, body)
					: this.tenantService.createTenant(area, realEstateId, tenantUnitId, body);
			// changeOwner ist im Backend ein Wechsel (beendet die laufende Rolle und legt eine neue an)
			// und wirft beim Update deterministisch ARA-2001 — für Änderungen gilt updateOwner.
			case UiRoleType.OWNER:
				return role.id
					? this.ownerService.updateOwner(area, realEstateId, tenantUnitId, role.id, body)
					: this.ownerService.createOwner(area, realEstateId, tenantUnitId, body);
			case UiRoleType.CONSUMPTION_INFO_RECEIVER:
				return role.id
					? this.consumptionInfoReceiverService.updateConsumptionInfoReceiver(area, realEstateId, tenantUnitId, role.id, body)
					: this.consumptionInfoReceiverService.createConsumptionInfoReceiver(area, realEstateId, tenantUnitId, body);
			case UiRoleType.VACANCY:
				return role.id
					? this.vacancyService.updateVacancy(area, realEstateId, tenantUnitId, role.id, this.buildVacancyDto(role, person, tenantUnitId))
					: this.vacancyService.createVacancy(area, realEstateId, tenantUnitId, this.buildVacancyDto(role, person, tenantUnitId));
			default:
				return of({} as PersistedRole);
		}
	}

	private buildRoleDto(role: PersonEditorRoleValue, person: Person, tenantUnitId: string): Resident {
		const validFrom = role.validFrom ?? DateUtil.minDate;
		const validTo = role.validTo ?? DateUtil.maxDate;
		const dto: Resident = {
			tenantUnitId,
			person,
			address: person.address,
			validFrom: this.dateUtil.getDateWithoutTimezone(validFrom).toISOString(),
			validTo: this.dateUtil.getDateWithoutTimezone(validTo).toISOString(),
		};
		if (role.roleType === UiRoleType.RESIDENT || role.roleType === UiRoleType.CONSUMPTION_INFO_RECEIVER) {
			dto.propertyManagementNumber = role.tenantNumber ?? undefined;
		}
		return dto;
	}

	private buildVacancyDto(role: PersonEditorRoleValue, person: Person, tenantUnitId: string): Vacancy {
		const validFrom = role.validFrom ?? DateUtil.minDate;
		const includePerson = this.hasRealPersonData(person);
		return {
			tenantUnitId,
			validFrom: this.dateUtil.getDateWithoutTimezone(validFrom).toISOString(),
			// Enddatum der Zeile übernehmen (nur leer → offen), sonst wird ein beendeter Leerstand wieder geöffnet.
			validTo: this.dateUtil.getDateWithoutTimezone(role.validTo ?? DateUtil.maxDate).toISOString(),
			person: includePerson ? person : undefined,
			address: includePerson ? person.address : undefined,
		};
	}

	/**
	 * Synthetische Ids (`vacancy0`, `new0`) zählen NICHT als echte Daten — sonst trägt ein
	 * Leerstands-DTO eine Phantom-Person mit nicht existierender Id.
	 */
	private hasRealPersonData(person: Person | undefined): boolean {
		const hasRealId = !!person?.id && !isSyntheticPersonId(person.id);
		return hasRealId || !!(person?.fullName?.trim() || person?.familyName?.trim() || person?.givenName?.trim() || person?.email?.trim());
	}

	/**
	 * Geschrieben wird nur bei NEUEN Rollen oder echter Änderung — sonst würde ein bestehendes
	 * PORTAL/NO_EED überschrieben, auch wenn der RoleSetting-GET beim Öffnen noch nicht zurück war.
	 */
	private persistResidentRoleSettings(savedRoles: SavedRole[], area: number): Observable<unknown> {
		const requests$: Observable<unknown>[] = [];
		for (const { role, saved } of savedRoles) {
			if (role.roleType !== UiRoleType.RESIDENT) {
				continue;
			}
			const receptionType = role.uviReception as ReceptionTypeEnum;
			const changed = (role.uviReception ?? null) !== (role.uviReceptionInitial ?? null);
			if (!receptionType || (role.id && !changed)) {
				continue;
			}
			const roleId = saved?.id ?? role.id ?? undefined;
			if (!roleId) {
				continue;
			}
			const roleSetting: RoleSetting = { roleId, roleType: RoleTypeEnum.RESIDENT, receptionType };
			requests$.push(this.energyEfficiencySettingService.createOrUpdateRoleSetting(area, roleId, roleSetting));
		}
		return requests$.length ? forkJoin(requests$) : of(null);
	}

	/**
	 * Berechtigung + Zugangscode für jede Rollenzeile, deren Häkchen gesetzt ist — Bestandsrollen
	 * eingeschlossen: „Code erneut versenden" und „Code nachträglich anlegen" laufen genau hierüber.
	 * Ohne Häkchen entsteht nichts (bis DEV-23651 entstand der Code ungefragt, nur die Mail blieb aus).
	 */
	private createPermissionsForSelectedRoles(request: PersistRolesRequest, saved: Map<PersonEditorRoleValue, PersistedRole>): Observable<unknown[]> {
		const requests$: Observable<unknown>[] = [];
		for (const role of request.roles) {
			// Leerstand und uVI-Empfänger können keinen Portal-Zugang haben (siehe permissionTypeForRole).
			if (!role.portalAccess || !canHavePortalAccess(role.roleType)) {
				continue;
			}
			// Neue Rollen bekommen ihre Id erst beim Speichern, Bestandsrollen bringen sie mit.
			const roleId = saved.get(role)?.id ?? role.id;
			if (!roleId) {
				continue;
			}
			const permission = this.buildPermission(role.roleType, roleId, request, request.person);
			if (!permission) {
				continue;
			}
			// Der Zugangscode entsteht mit dem Aufruf; versendet wird er nur an eine vorhandene Adresse.
			const sendMail = !!permission.email?.trim();
			requests$.push(this.permissionsService.createPermission(permission, sendMail).pipe(catchError(() => of(null))));
		}
		return requests$.length === 0 ? of([]) : forkJoin(requests$);
	}

	/** `null`, wenn der Rollentyp keinen Portal-Zugang haben darf — dann entsteht auch kein Code. */
	private buildPermission(roleType: RoleType | null, roleId: string, request: PersistRolesRequest, person: Person): Permission | null {
		const permissionType = permissionTypeForRole(roleType);
		if (!permissionType) {
			return null;
		}
		// Mit Code = denselben erneut verschicken, ohne Code = neuen anlegen.
		const authCode = request.portalAccessCodeByRoleId?.[roleId] ?? '';
		return new Permission(
			'',
			person?.givenName ?? '',
			person?.familyName ?? person?.fullName ?? '',
			authCode,
			new Date().toISOString(),
			permissionType,
			roleId,
			'',
			request.tenantUnitId,
			request.area,
			person?.email
		);
	}

	// ---------------------------------------------------------------------------------------------
	// Länder
	// ---------------------------------------------------------------------------------------------

	buildCountryOptions(): TranslatedCountry[] {
		return Object.values(CountryEnum)
			.filter((country) => country !== CountryEnum.UNKNOWN)
			.map((country) => ({ value: country, translation: this.transloco.translate('country.' + country.toLowerCase()) }))
			.sort((a, b) => a.translation.localeCompare(b.translation));
	}

	filterCountries(countries: TranslatedCountry[], search: string | null | undefined): TranslatedCountry[] {
		const query = search?.toLowerCase().trim();
		if (!query) {
			return countries;
		}
		return countries.filter((country) => country.translation.toLowerCase().includes(query));
	}

	// ---------------------------------------------------------------------------------------------
	// Labels
	// ---------------------------------------------------------------------------------------------

	getRoleTypeLabel(roleType: string | null | undefined): string {
		return this.rolesDisplay.roleTypeLabel(roleType);
	}
}
